import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import DOMPurify from "dompurify";
import "./Careers.css";
import { Helmet } from "react-helmet";

const ApplyJob = () => {
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      currentLocation: "",
      jobTitle: "",
      totalExperience: "",
      file: null,
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .matches(/^[a-zA-Z\s]+$/, "Invalid name (letters & spaces only)")
        .required("First Name is required"),
      lastName: Yup.string()
        .matches(/^[a-zA-Z\s]+$/, "Invalid name (letters & spaces only)")
        .required("Last Name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      phone: Yup.string().matches(/^[0-9]{10}$/, 'Phone number must be 10 digits'),
      currentLocation: Yup.string().matches(/^[a-zA-Z\s]+$/, 'Invalid location (letters and spaces only)').required("Current Location is required"),
      jobTitle: Yup.string().matches(/^[a-zA-Z\s\-.]+$/, 'Invalid job title (letters, spaces, hyphens, and periods only)').required("Job Title is required"),
      totalExperience: Yup.string() .matches(/^\d+\s?(years?|months?)?(\s\d+\s?months?)?$/, 'Invalid experience format').required("Total Experience is required"),
      file: Yup.mixed()
        .required("Resume file is required")
        .test("fileType", "Only .pdf and .doc files are allowed", (value) => {
          return (
            value &&
            [
              "application/pdf",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ].includes(value.type)
          );
        }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const apiUrl = `${process.env.REACT_APP_NEWSLETTER_API_URL}/apply-resume`;

        navigate("/thank-you");

        const ipResponse = await fetch("https://api64.ipify.org?format=json");
        const ipData = await ipResponse.json();
        const userIp = ipData.ip;

        const sanitizedValues = {
          firstName: DOMPurify.sanitize(values.firstName),
          lastName: DOMPurify.sanitize(values.lastName),
          email: DOMPurify.sanitize(values.email),
          phone: DOMPurify.sanitize(values.phone),
          currentLocation: DOMPurify.sanitize(values.currentLocation),
          jobTitle: DOMPurify.sanitize(values.jobTitle),
          totalExperience: DOMPurify.sanitize(values.totalExperience),
          subject: DOMPurify.sanitize(values.subject),
          message: DOMPurify.sanitize(values.message),
        };

        const formData = new FormData();
        formData.append("to", "Cync-Website-Lead@cyncsoftware.com");
        formData.append("firstName", sanitizedValues.firstName);
        formData.append("lastName", sanitizedValues.lastName);
        formData.append("email", sanitizedValues.email);
        formData.append("phone", sanitizedValues.phone);
        formData.append("currentLocation", sanitizedValues.currentLocation);
        formData.append("jobTitle", sanitizedValues.jobTitle);
        formData.append("totalExperience", sanitizedValues.totalExperience);
        formData.append("resume", values.file); // Ensure file is correctly set
        formData.append("ip", userIp);

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            // "x-api-key": process.env.REACT_APP_X_API_KEY, 
          },
          body: formData,
        });

        if (response.ok) {
          resetForm();
        } else {
          console.error("Error sending email");
        }
      } catch (error) {
        console.error("Error sending email", error);
      }
    },
  });

  return (
    <div className="apply-job-page">
      <Helmet>
        <title>Submit Your Resume | Idexcel</title>
        <meta
          content="current openings usa, current openings india"
          name="keywords"
        />
        <meta
          content="Ready to explore a career with Idexcel? Click here and explore our job openings."
          name="description"
        />
      </Helmet>
      <section
        className="banner-section"
        style={{
          background:
            "url('https://www.idexcel.com/img/banners/case-studies-banner.jpg') no-repeat center center",
          backgroundSize: "cover",
        }}
      >
        <Container fluid style={{ display: "contents" }}>
          <Row>
            <Col lg={12}>
              <div className="banner-overlay">
                {/* You can add any overlay content here if needed */}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section>
        <Container className="my-5 px-xl-5">
          <div className="apply-job-form text-center">
            <h2 className="text-center"> Submit Resume</h2>
            <Form className="mt-4" onSubmit={formik.handleSubmit}>
              <Row className="d-flex justify-content-center flex-column">
                <Col className="my-2 input-tag">
                  <input
                    className="w-100 bg-white my-1 py-2 px-3 border rounded"
                    type="text"
                    placeholder="First Name*"
                    {...formik.getFieldProps("firstName")}
                  />
                  {formik.touched.firstName && formik.errors.firstName && (
                    <div className="text-danger">{formik.errors.firstName}</div>
                  )}
                </Col>
                <Col className="my-2 input-tag">
                  <input
                    className="w-100 bg-white my-1 py-2 px-3 border rounded "
                    type="text"
                    placeholder="Last Name*"
                    {...formik.getFieldProps("lastName")}
                  />
                  {formik.touched.lastName && formik.errors.lastName && (
                    <div className="text-danger">{formik.errors.lastName}</div>
                  )}
                </Col>
                <Col className=" my-2 input-tag">
                  <input
                    className="w-100 bg-white my-1 py-2 px-3  border rounded"
                    type="email"
                    placeholder="Email*"
                    {...formik.getFieldProps("email")}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className="text-danger">{formik.errors.email}</div>
                  )}
                </Col>
                <Col className="my-2">
                  <input
                    className="w-100 bg-white my-1 py-2 px-3  border rounded"
                    type="text"
                    placeholder="Phone Number"
                    {...formik.getFieldProps("phone")}
                  />
                  {formik.touched.phone  && (
                    <div className="text-danger">{formik.errors.phone}</div>
                  )}
                </Col>
                <Col className="my-2">
                  <input
                    className="w-100 bg-white my-1 py-2 px-3  border rounded"
                    type="text"
                    placeholder="Current Location*"
                    {...formik.getFieldProps("currentLocation")}
                  />
                  {formik.touched.currentLocation &&
                    formik.errors.currentLocation && (
                      <div className="text-danger">
                        {formik.errors.currentLocation}
                      </div>
                    )}
                </Col>
                <Col className="my-2">
                  <input
                    className="w-100 bg-white my-1 py-2 px-3  border rounded"
                    type="text"
                    placeholder="Job Title*"
                    {...formik.getFieldProps("jobTitle")}
                  />
                  {formik.touched.jobTitle && formik.errors.jobTitle && (
                    <div className="text-danger">{formik.errors.jobTitle}</div>
                  )}
                </Col>
                <Col className="my-2">
                  <input
                    className="w-100 bg-white my-1 py-2 px-3  border rounded"
                    type="text"
                    placeholder="Total Experience*"
                    {...formik.getFieldProps("totalExperience")}
                  />
                  {formik.touched.totalExperience &&
                    formik.errors.totalExperience && (
                      <div className="text-danger">
                        {formik.errors.totalExperience}
                      </div>
                    )}
                </Col>
                {/* <Col className="my-2">
                  <label className="w-100 bg-white my-1 py-2 px-3 border rounded d-flex align-items-center justify-content-between">
                    <span className="text-muted">
                      Choose a file* (.pdf, .doc)
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.doc"
                      onChange={(event) => {
                        formik.setFieldValue(
                          "file",
                          event.currentTarget.files[0]
                        );
                      }}
                      style={{ display: "none" }} // Hide the default file input
                    />
                    <span className="btn btn-secondary">Choose File</span>
                  </label>
                  {formik.touched.file && formik.errors.file && (
                    <div className="text-danger">{formik.errors.file}</div>
                  )}
                </Col> */}
                <Col className=" col-12 my-2">
                  <div className="file-input-wrapper position-relative">
                    <input
                      className="w-100 bg-white my-1 py-2 px-3  border rounded"
                      style={{
                        padding: "5px 0px",
                        position: "absolute",
                        opacity: 0,
                        zIndex: 2,
                        cursor: "pointer",
                      }}
                      type="file"
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        if (file) {
                          // Dynamically update the placeholder text with the file name
                          const placeholder =
                            event.currentTarget.nextElementSibling.querySelector(
                              ".text-muted"
                            );
                          placeholder.textContent = file.name;
                          formik.setFieldValue("file", file);
                        }
                      }}
                    />
                    <div
                      className="file-placeholder w-100 bg-white px-3 border rounded d-flex align-items-center justify-content-between"
                      style={{
                        padding: "5px 0px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                      }}
                    >
                      <span className="text-muted">
                        {formik.values.file
                          ? formik.values.file.name
                          : "Choose a file* (.pdf, .doc)"}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          const fileInput =
                            document.querySelector('input[type="file"]');
                          if (formik.values.file) {
                            // Clear the file
                            formik.setFieldValue("file", null);
                            const placeholder =
                              document.querySelector(".text-muted");
                            if (placeholder)
                              placeholder.textContent =
                                "Choose a file* (.pdf, .doc)";
                            fileInput.value = ""; // Clear the file input value
                          } else {
                            // Trigger file input click
                            fileInput.click();
                          }
                        }}
                      >
                        {formik.values.file ? "Remove" : "Choose"}
                      </button>
                    </div>
                  </div>
                  {formik.touched.file && formik.errors.file && (
                    <div className="text-danger">{formik.errors.file}</div>
                  )}
                </Col>
              </Row>
              <Button className="button mt-4" type="submit">
                Submit
              </Button>
            </Form>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default ApplyJob;
